// ABOUTME: React Query hook for fetching overbooking allowance configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/overbooking-allowances, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {OverbookingAllowanceApiResponse, OverbookingAllowanceConfig} from '../types/overbookingAllowance'
import {asNotInherited, emptyApiRow} from '../utils/emptyConfig'

const transformResponse = (response: OverbookingAllowanceApiResponse): OverbookingAllowanceConfig => {
  const entries = (response.entries?.value ?? []).map((entry) => ({
    customerTier: entry.customerTier,
    overbookingPercent: entry.overbookingPercent,
    leadTimeValue: entry.leadTime.value,
    leadTimeUomId: entry.leadTime.uom?.id ?? '',
    leadTimeReferenceEvent: entry.leadTime.referenceEvent
      ? entry.leadTime.referenceEvent.charAt(0).toLowerCase() + entry.leadTime.referenceEvent.slice(1)
      : '',
  }))
  return {
    id: response.id,
    entries,
    overrides: {
      entries: response.entries?.isOverridden ?? false,
    },
    parentValues: {entries},
    _meta: {
      scopeId: response.scopeId,
      isInherited: !(response.entries?.isOverridden ?? false),
      resolvedFrom: null,
      createdAt: response._meta.createdAt,
      updatedAt: response._meta.updatedAt,
      createdBy: response._meta.createdBy,
      updatedBy: response._meta.updatedBy,
    },
  }
}

export const useOverbookingAllowanceConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: OverbookingAllowanceConfig | undefined; isLoading: boolean; error: Error | null} => {
  const normalizedBusinessLine = businessLine?.toUpperCase()
  const {data: rawData, isLoading, error} = useQuery<OverbookingAllowanceApiResponse[], Error>({
    queryKey: ['overbookingAllowanceConfig', countryId, normalizedBusinessLine],
    queryFn: async () => {
      const res = await api.get<OverbookingAllowanceApiResponse[]>('/tuc-configs/overbooking-allowances', {
        params: {countryId, businessLine: normalizedBusinessLine},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootRawConfig = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined
    const rootConfig = rootRawConfig ? transformResponse(rootRawConfig) : undefined
    const parentValues = rootConfig ? {entries: rootConfig.entries} : undefined

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) {
      const config = transformResponse(match)
      return parentValues ? {...config, parentValues} : config
    }

    if (!rootRawConfig || !rootConfig) {
      return asNotInherited(transformResponse(emptyApiRow<OverbookingAllowanceApiResponse>(scopeId)))
    }

    return {
      ...rootConfig,
      id: undefined,
      parentValues: parentValues ?? {entries: rootConfig.entries},
      _meta: {...rootConfig._meta, isInherited: true},
      overrides: {entries: false},
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
